"use client";

/**
 * Quiet "search in flight" shell (B-P0-09 v2, 2026-05-15).
 *
 * Renders the exact same outer layout as the live ComparisonStream
 * surface using the SAME persistent <Hero loadingState="loading"/>
 * and <ComparisonRow loadingState="loading"/> components. When
 * Next.js swaps loading.tsx out for the streaming surface the user
 * sees ONE continuous canvas - identical mounts, no skeleton-to-
 * real component swap, no remount blink.
 *
 * Used by:
 *   - app/compare/loading.tsx: while the server component is doing
 *     the clarify call.
 *   - ClarificationGrid (chip-pick handoff): while Next.js is
 *     navigating to the new query.
 *
 * Optional `intent` prop: chip-pick carries product_label, brand,
 * so the H1 shows the real product name. Otherwise the Hero falls
 * back to "Searching…".
 */

import type { EquivalenceGroup, ParsedIntent } from "@/lib/api/types";

import { ComparisonRow } from "./v2/ComparisonRow";
import { Hero } from "./v2/Hero";

interface Props {
  intent?: ParsedIntent;
  postcode?: string;
  skeletonCount?: number;
}

export function ComparisonShell({
  intent,
  postcode = "2000",
  skeletonCount = 6,
}: Props) {
  const heroGroup: EquivalenceGroup = {
    canonical_name: intent?.product_label || intent?.raw_query || "Searching…",
    canonical_brand: intent?.brand ?? null,
    canonical_gtin: null,
    canonical_mpn: intent?.model_number ?? null,
    products: [],
    delivery_confirmed: false,
  };

  return (
    <main className="cmp-surface relative mx-auto min-h-screen max-w-4xl bg-background px-5 pb-10 sm:px-8 sm:pb-14">
      <Hero
        group={heroGroup}
        postcode={postcode}
        loadingState="loading"
        progress={{ loaded: 0, failed: 0, total: 0, isDone: false }}
      />

      <section className="mt-12">
        <div className="mb-5 flex items-baseline justify-between">
          <h2 className="text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Other retailers
          </h2>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
            Ranked by TFP
          </p>
        </div>
        {/* 2026-05-31: single column to match the new compact-tier
            full-width layout. Keeps the loading -> live-search
            transition free of layout shift. */}
        <div className="space-y-3">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <ComparisonRow key={i} rank={i + 1} loadingState="loading" />
          ))}
        </div>
      </section>
    </main>
  );
}
